import { Box, TextField, Typography, Button, Paper, InputAdornment } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory'; // For the investment title icon
import { updateCompany } from "../../../../features/company/companySlice";
import { resetDraft, updateField } from "../../../../features/editCompany/editCompanySlice";

export default function InvestmentStep() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const dispatch = useDispatch();
  const draft = useSelector((state) => state.editCompany.draft);

  const handleSave = () => {
    dispatch(
      updateCompany({
        companyId,
        updates: {
          name: draft.name,
          sector: draft.sector,
          location: draft.location,
          reportingCurrency: draft.reportingCurrency,
          description: draft.description,
          ownership: {
            partners: draft.partners,
            boardRepresentative: draft.boardRepresentative,
            fundFamily: draft.fundFamily,
            vintageYear: draft.vintageYear,
            grade: draft.grade,
            rank: draft.rank,
          },
          investment: {
            ownershipPct: draft.ownershipPct,
            investedCapital: draft.investedCapital,
            currentReserves: draft.currentReserves,
            totalCapitalBudget: draft.totalCapitalBudget,
            currentValue: draft.currentValue,
            realisedValue: draft.realisedValue,
          },
        },
      })
    );
    dispatch(resetDraft());
    navigate(`/company/${companyId}`);
  };

  // Consistent styling for the "pill" inputs
  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#fff',
      height: '40px',
    },
    '& .MuiInputBase-input': { 
      fontSize: '0.9rem',
      fontWeight: 500
    },
    '& legend': { display: 'none' },
  };

  const labelStyle = {
    display: 'block',
    mb: 0.5,
    color: '#888',
    fontWeight: 500,
    fontSize: '0.85rem'
  };

  const adornmentStyle = {
    '& .MuiTypography-root': { 
        color: '#ccc', 
        fontSize: '0.9rem',
        mr: 0.5 
    }
  };

  return (
    <Box sx={{minHeight:"100vh", pb: 10, position: "relative" }}>
      {/* 🔹 Main Card */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: 800,
          mx: "auto",
          mt: 4,
          p: 4,
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          bgcolor: "#FAFAFA"
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={4}>
          <WorkHistoryIcon sx={{ fontSize: 18, color: '#bcaaa4' }} />
          <Typography variant="subtitle2" fontWeight={700} color="#333">
            Investment
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          {/* Row 1: Ownership & Invested Capital */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography sx={labelStyle}>% Ownership</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.ownershipPct || "47.7"}
                onChange={(e) => dispatch(updateField({ field: "ownershipPct", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>%</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Invested Capital</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.investedCapital || "420,000,000"}
                onChange={(e) => dispatch(updateField({ field: "investedCapital", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>$</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
          </Box>

          {/* Row 2: Reserves & Budget */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography sx={labelStyle}>Current Reserves</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.currentReserves || "420,000,000"}
                onChange={(e) => dispatch(updateField({ field: "currentReserves", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>$</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Total Capital Budget</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.totalCapitalBudget || "420,000,000"}
                onChange={(e) => dispatch(updateField({ field: "totalCapitalBudget", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>$</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
          </Box>

          {/* Row 3: Current & Realised Value */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography sx={labelStyle}>Current Value</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.currentValue || "420,000,000"}
                onChange={(e) => dispatch(updateField({ field: "currentValue", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>$</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
            <Box>
              <Typography sx={labelStyle}>Realized Value</Typography>
              <TextField
                fullWidth
                size="small"
                value={draft.realisedValue || "420,000,000"}
                onChange={(e) => dispatch(updateField({ field: "realisedValue", value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={adornmentStyle}>$</InputAdornment>,
                }}
                sx={fieldStyle}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* 🔷 Fixed Bottom Action Bar */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          zIndex: 1000
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/company/${companyId}/edit/ownership`)}
          sx={{
            color: '#444',
            borderColor: '#e0e0e0',
            textTransform: 'none',
            borderRadius: '20px',
            px: 3,
            '&:hover': { borderColor: '#ccc', bgcolor: 'transparent', color:"black" }
          }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: '#005682',
            textTransform: 'none',
            borderRadius: '20px',
            px: 4,
            fontWeight: 600,
            '&:hover': { bgcolor: '#004466' }
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}